import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Paper,
    TextField,
    InputAdornment,
    Chip,
    Button,
    Checkbox,
    Link,
    Tabs,
    Tab,
    IconButton,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddPatientModal from "./AddPatientModal";
import AxiosInstance from '../configs/axiosConfig';
import { useSnackbar } from '../contexts/SnackbarContext';

const PRIMARY_COLOR = "#003595";

interface Patient {
    id: string;
    userId: string;
    name: string;
    registrationDate: string;
    registrationTime: string;
    fullDateTime: string;
    evaluationDuration: string;
    lastSession: string;
    hasExitQuestionnaire: boolean;
    checked: boolean;
}

interface PatientsTableProps {
    showAll?: boolean;
    maxRows?: number;
}

export default function PatientsTable({ showAll = false, maxRows = 10 }: PatientsTableProps) {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [patientList, setPatientList] = useState<Patient[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const { showError } = useSnackbar();
    
    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(showAll ? 10 : maxRows);
    
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    // Fetch patients from API
    const fetchPatients = async (dateFilter?: string) => {
        try {
            setLoading(true);
            const params = dateFilter ? { date: dateFilter } : {};
            const response = await AxiosInstance.get('/patients?call=webapp', { params });
            
            // Transform API data to match your Patient interface
            const transformedData = response.data.map((patient: any) => {
                // Parse the date from registeredOn
                const registrationDate = patient.registeredOn 
                    ? new Date(patient.registeredOn).toLocaleDateString() 
                    : "N/A";
                
                // Parse the time from registrationTimeStamp
                const registrationTime = patient.registrationTimeStamp;
                
                // Combine both for full datetime display
                const fullDateTime = patient.registeredOn && patient.registrationTimeStamp
                    ? `${registrationDate} ${registrationTime}`
                    : registrationDate !== "N/A" 
                        ? registrationDate 
                        : registrationTime !== "N/A" 
                            ? registrationTime 
                            : "N/A";
                
                // Format evaluation duration for display
                const evaluationDuration = patient.evaluationDuration ? patient.evaluationDuration : "N/A";
                
                // Format last session
                const lastSession = patient.lastSession 
                    ? new Date(patient.lastSession).toLocaleDateString()
                    : "N/A";
                
                return {
                    id: patient.oktaID,
                    userId: patient.userId,
                    registrationDate,
                    registrationTime,
                    fullDateTime,
                    evaluationDuration,
                    lastSession,
                    hasExitQuestionnaire: patient.hasExitQuestionnaire,
                    checked: false,
                };
            });
            
            setPatientList(transformedData);
        } catch (error) {
            console.debug("error fetching patients>>>", error);
            showError('Failed to fetch patients. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchPatients(activeFilter);
    }, []);

    // Reset pagination when search term or filter changes
    useEffect(() => {
        setPage(0);
    }, [searchTerm, activeFilter]);

    const handleFilterChange = (event: React.SyntheticEvent, newValue: string) => {
        setActiveFilter(newValue);
        // Fetch patients with the new date filter
        fetchPatients(newValue);
    };

    const handleCheckboxChange = (patientId: string) => {
        setPatientList((prev) =>
            prev.map((patient) =>
                patient.id === patientId
                    ? { ...patient, checked: !patient.checked }
                    : patient
            )
        );
    };

    const handleRefresh = () => {
        fetchPatients(activeFilter);
    };

    const handlePatientAdded = () => {
        // Refresh the list when a new patient is added
        fetchPatients(activeFilter);
    };

    const handleViewAllPatients = () => {
        navigate('/patients');
    };

    // Pagination handlers
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Filter patients based on search term
    const filteredPatients = patientList.filter(patient =>
        patient.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.registrationDate.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.registrationTime.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.fullDateTime.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.evaluationDuration.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.lastSession.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply pagination
    const paginatedPatients = showAll 
        ? filteredPatients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : filteredPatients.slice(0, maxRows);

    return (
        <Box>
            {!showAll && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            color: "#333",
                            fontWeight: 500,
                            fontSize: "1.1rem",
                        }}
                    >
                        Study Patients
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "#666",
                            textTransform: "uppercase",
                            fontSize: "0.75rem",
                            letterSpacing: 1,
                            cursor: "pointer",
                            "&:hover": {
                                color: PRIMARY_COLOR,
                            },
                        }}
                        onClick={handleViewAllPatients}
                    >
                        VIEW ALL PATIENTS
                    </Typography>
                </Box>
            )}

            <Paper
                elevation={1}
                sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                }}
            >
                {/* Study Header */}
                <Box
                    sx={{
                        p: 2,
                        bgcolor: "#f8f9fa",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: "#333",
                            fontWeight: 500,
                        }}
                    >
                        Multifocal Contact Lens Study
                    </Typography>
                    <Chip
                        label={patientList.length.toString()}
                        size="small"
                        sx={{
                            bgcolor: "#FFC107",
                            color: "#000",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                        }}
                    />
                </Box>

                {/* Search and Controls */}
                <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <TextField
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "#666", fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: 300,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    bgcolor: "#f5f5f5",
                                    border: "none",
                                    "& fieldset": {
                                        border: "none",
                                    },
                                },
                            }}
                        />
                        <Button
                            variant="contained"
                            sx={{
                                bgcolor: PRIMARY_COLOR,
                                "&:hover": {
                                    bgcolor: "#002566",
                                },
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                                textTransform: "uppercase",
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                letterSpacing: 1,
                            }}
                            onClick={handleOpen}
                        >
                            ADD PATIENT
                        </Button>
                    </Box>

                    {/* Filter Tabs */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Tabs
                            value={activeFilter}
                            onChange={handleFilterChange}
                            sx={{
                                minHeight: 32,
                                "& .MuiTab-root": {
                                    textTransform: "none",
                                    fontWeight: 500,
                                    fontSize: "0.85rem",
                                    color: "#666",
                                    minHeight: 32,
                                    padding: "6px 12px",
                                    minWidth: "auto",
                                },
                                "& .Mui-selected": {
                                    color: `${PRIMARY_COLOR} !important`,
                                },
                                "& .MuiTabs-indicator": {
                                    backgroundColor: PRIMARY_COLOR,
                                    height: 2,
                                },
                            }}
                        >
                            <Tab label="All" value="all" />
                            <Tab label="Today" value="today" />
                            <Tab label="Week" value="week" />
                            <Tab label="Month" value="month" />
                        </Tabs>
                        <IconButton 
                            size="small" 
                            sx={{ color: "#666" }}
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* Patient List */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        sx={{
                                            color: "#ccc",
                                            "&.Mui-checked": {
                                                color: PRIMARY_COLOR,
                                            },
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#333",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        Patient ID
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#333",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        Created On
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#333",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        Evaluation Duration
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#333",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        Last Session
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#333",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        Completed
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#333",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        Actions
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#333",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        Standard Care
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={24} />
                                    </TableCell>
                                </TableRow>
                            ) : filteredPatients.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#666' }}>
                                        <Typography variant="body2">
                                            {searchTerm ? 'No patients found matching your search' : 'No patients found'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedPatients.map((patient) => (
                                    <TableRow
                                        key={patient.id}
                                        sx={{
                                            "&:hover": {
                                                bgcolor: "#f9f9f9",
                                            },
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={patient.checked}
                                                onChange={() => handleCheckboxChange(patient.id)}
                                                size="small"
                                                sx={{
                                                    color: "#ccc",
                                                    "&.Mui-checked": {
                                                        color: PRIMARY_COLOR,
                                                    },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#333",
                                                    fontSize: "0.85rem",
                                                }}
                                            >
                                                {patient.id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#333",
                                                    fontSize: "0.85rem",
                                                }}
                                            >
                                                {patient.fullDateTime}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#333",
                                                    fontSize: "0.85rem",
                                                }}
                                            >
                                                {patient.evaluationDuration}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#333",
                                                    fontSize: "0.85rem",
                                                }}
                                            >
                                                {patient.lastSession}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#333",
                                                    fontSize: "0.85rem",
                                                    fontWeight: patient.hasExitQuestionnaire ? 600 : 400,
                                                }}
                                            >
                                                {patient.hasExitQuestionnaire ? "Yes" : "No"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Link
                                                component="button"
                                                underline="always"
                                                sx={{
                                                    color: "#666",
                                                    fontSize: "0.8rem",
                                                    "&:hover": {
                                                        color: PRIMARY_COLOR,
                                                    },
                                                    background: "none",
                                                    border: "none",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => navigate(`/patients/${patient.userId}/${patient.id}`)}
                                            >
                                                View results
                                            </Link>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={{
                                                    borderColor: PRIMARY_COLOR,
                                                    color: PRIMARY_COLOR,
                                                    textTransform: "none",
                                                    fontSize: "0.75rem",
                                                    padding: "4px 12px",
                                                    "&:hover": {
                                                        borderColor: "#002566",
                                                        bgcolor: "#f5f5f5",
                                                    },
                                                }}
                                                onClick={() => navigate(`/patients/standardcare/${patient.userId}/${patient.id}`)}
                                            >
                                                Update
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {/* Pagination - only show when showAll is true */}
                {showAll && (
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={filteredPatients.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            borderTop: '1px solid #e0e0e0',
                            '& .MuiTablePagination-toolbar': {
                                paddingLeft: 2,
                                paddingRight: 2,
                            },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: '0.875rem',
                                color: '#666',
                            },
                        }}
                    />
                )}
            </Paper>
            <AddPatientModal 
                open={open} 
                onClose={handleClose} 
                onPatientAdded={handlePatientAdded}
            />
        </Box>
    );
}